import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function SLAPage() {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-brand-primary text-white px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-sm opacity-80 hover:opacity-100 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to app
          </Link>
          <div className="text-sm font-bold tracking-wide">Service Level Agreement</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 prose prose-sm">
        <h1 className="text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          Service Level Agreement
        </h1>
        <p className="text-zinc-600 mb-8">
          Effective: 14 Feb 2026 &middot; Governs all paid PipeGuide subscriptions.
        </p>

        <section className="bg-white border border-zinc-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-zinc-900 mb-3">1. Uptime commitment</h2>
          <p className="text-zinc-700 mb-3">
            We commit to a monthly uptime of <strong>99.5%</strong> for the PipeGuide application
            (login, dashboard, API, live transcription). Live transcripts depend on Recall.ai; outages
            attributable solely to upstream providers are excluded.
          </p>
          <table className="w-full text-sm border border-zinc-200 rounded">
            <thead className="bg-zinc-50">
              <tr><th className="text-left p-2">Plan</th><th className="text-left p-2">Uptime target</th><th className="text-left p-2">Service credit</th></tr>
            </thead>
            <tbody>
              <tr className="border-t border-zinc-100"><td className="p-2">Starter</td><td className="p-2">99.0%</td><td className="p-2">5% of monthly fee per 1% below</td></tr>
              <tr className="border-t border-zinc-100"><td className="p-2">Pro</td><td className="p-2">99.5%</td><td className="p-2">10% of monthly fee per 1% below</td></tr>
              <tr className="border-t border-zinc-100"><td className="p-2">Business</td><td className="p-2">99.9%</td><td className="p-2">20% of monthly fee per 1% below (capped at 50%)</td></tr>
            </tbody>
          </table>
          <p className="text-xs text-zinc-500 mt-3">
            Uptime is measured externally against{' '}
            <a href="/status" className="text-brand-primary">status.pipeguide.tech</a> probes.
            Scheduled maintenance (announced 48 hours in advance) is excluded.
          </p>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-zinc-900 mb-3">2. Support response times</h2>
          <ul className="space-y-2 text-zinc-700">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /><span><strong>Starter:</strong> first response within <strong>24 business hours</strong>, Mon–Fri 09:00–17:00 SAST.</span></li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /><span><strong>Pro:</strong> first response within <strong>4 business hours</strong>.</span></li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /><span><strong>Business:</strong> first response within <strong>1 business hour</strong>, plus a dedicated Slack channel.</span></li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-zinc-900 mb-3">3. Incident definitions</h2>
          <ul className="space-y-1.5 text-zinc-700 text-sm">
            <li><strong>Severity 1 (Critical):</strong> the whole service is down, or no customer can log in. Target resolution: 4 hours.</li>
            <li><strong>Severity 2 (Major):</strong> a core feature (live transcription, AI scoring, billing) is unavailable for &gt;25% of users. Target resolution: 8 hours.</li>
            <li><strong>Severity 3 (Minor):</strong> a non-critical feature degraded or a workaround exists. Target resolution: 2 business days.</li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-zinc-900 mb-3">4. Service credit claims</h2>
          <p className="text-zinc-700">
            To claim a service credit, email <a href="mailto:support@pipeguide.tech" className="text-brand-primary font-semibold">support@pipeguide.tech</a>{' '}
            within 30 days of the incident with the date(s) and affected sessions. Credits are issued as
            a discount on your next invoice. Credits cannot be refunded as cash.
          </p>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-zinc-900 mb-3">5. Exclusions</h2>
          <p className="text-zinc-700 mb-2">SLA does not cover outages caused by:</p>
          <ul className="list-disc pl-6 text-sm text-zinc-700 space-y-1">
            <li>Customer's own network, browser, or meeting platform settings (e.g. Teams admin blocking bots)</li>
            <li>Force majeure (natural disasters, government action, war, internet backbone outages)</li>
            <li>Misuse of the platform in violation of the Terms of Service</li>
            <li>Outages of third-party sub-processors beyond commercially reasonable mitigation</li>
            <li>Scheduled maintenance windows announced at least 48 hours in advance</li>
          </ul>
        </section>

        <p className="text-xs text-zinc-500 text-center">
          For enterprise SLAs (custom uptime targets, dedicated infrastructure, 24/7 on-call), contact{' '}
          <a href="mailto:sales@pipeguide.tech" className="text-brand-primary">sales@pipeguide.tech</a>.
        </p>
      </main>
    </div>
  );
}
